import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';

import useFetchModelInstancesCursorPagination, {
  FETCH_MODEL_INSTANCES_CURSOR_PAGINATION_KEY,
  fetchModelInstancesCursorPaginationApi,
} from '../../hooks/api/useFetchModelInstancesCursorPagination';
import useFetchModelInstancesCursorPaginationMeta from '../../hooks/api/useFetchModelInstancesCursorPaginationMeta';
import {
  buildLevelFilters,
  parseParentKey,
} from '../reference-tree-select-async/treeUtils';

export interface ITreeTableRow {
  id: string;
  _hasChildren?: boolean;
  _placeholder?: boolean;
  children?: ITreeTableRow[];
  [key: string]: any;
}

interface IParams {
  enabled: boolean;
  modelIds?: string[];
  scopeFilter?: any; // list scope (json-logic object)
  rootFilter?: any;
  childKey?: string;
  parentKey?: string;
  hasChildrenKey?: string;
  childrenPageSize?: number;
  page: number;
  size: number;
}

const STALE_TIME = 5 * 60 * 1000;
const MAX_CHILD_PAGES = 50; // safety cap when loading a node's children

/**
 * Recursively set the children of the row matching `targetId` (immutable).
 */
const setChildrenById = (
  rows: ITreeTableRow[],
  targetId: string,
  children: ITreeTableRow[]
): ITreeTableRow[] =>
  rows.map(row => {
    if (row.id === targetId) return { ...row, children };
    if (row.children) {
      return {
        ...row,
        children: setChildrenById(row.children, targetId, children),
      };
    }
    return row;
  });

/**
 * Lazily loads a hierarchy for the TreeTable: fetches the current page of roots
 * (+ their total for pagination) and each node's children on demand.
 */
export const useTreeTableTree = (params: IParams) => {
  const {
    enabled,
    modelIds,
    scopeFilter,
    rootFilter,
    childKey,
    parentKey,
    hasChildrenKey,
    childrenPageSize,
    page,
    size,
  } = params;

  const queryClient = useQueryClient();

  const { arrayField: parentArrayField, field: parentField } = useMemo(
    () => parseParentKey(parentKey),
    [parentKey]
  );

  const rootFiltersStr = useMemo(
    () =>
      enabled
        ? buildLevelFilters({
            parentTermId: null,
            scopeFilter,
            rootFilter,
            parentArrayField,
            parentField,
          })
        : undefined,
    [enabled, scopeFilter, rootFilter, parentArrayField, parentField]
  );

  const canFetch = enabled && !!modelIds && modelIds.length > 0;

  const { modelInstances: rootsResp, isLoadingModelInstances } =
    useFetchModelInstancesCursorPagination({
      params: { modelIds, filters: rootFiltersStr, page, size },
      options: {
        enabled: canFetch,
        placeholderData: prev => prev,
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
      },
    });

  const { modelInstancesMeta: rootsMeta } =
    useFetchModelInstancesCursorPaginationMeta({
      params: { modelIds, filters: rootFiltersStr, size },
      options: {
        enabled: canFetch,
        placeholderData: prev => prev,
        refetchOnWindowFocus: false,
      },
    });

  const toRow = useCallback(
    (instance: any): ITreeTableRow => {
      const hasChildren = hasChildrenKey
        ? !!get(instance, hasChildrenKey)
        : false;
      const row: ITreeTableRow = { ...instance, _hasChildren: hasChildren };
      // A placeholder child makes antd render the expander for a not-yet-loaded
      // parent; it's replaced by the real children on expand.
      if (hasChildren) {
        row.children = [
          { id: `${instance.id}__placeholder`, _placeholder: true },
        ];
      }
      return row;
    },
    [hasChildrenKey]
  );

  const [treeData, setTreeData] = useState<ITreeTableRow[]>([]);
  const loadedRef = useRef<Set<string>>(new Set());

  // Rebuild roots whenever the fetched page changes; loaded children are dropped
  // (they belong to the previous page / scope).
  useEffect(() => {
    setTreeData((rootsResp?.data ?? []).map(toRow));
    loadedRef.current = new Set();
  }, [rootsResp, toRow]);

  const loadChildren = useCallback(
    async (record: ITreeTableRow) => {
      if (loadedRef.current.has(record.id)) return;
      loadedRef.current.add(record.id);

      const code = childKey ? get(record, childKey) : undefined;
      if (code === undefined || code === null) return;

      const filters = buildLevelFilters({
        parentTermId: String(code),
        scopeFilter,
        rootFilter,
        parentArrayField,
        parentField,
      });

      const all: any[] = [];
      let pageNo = 1;
      let hasNext = true;
      while (hasNext && pageNo <= MAX_CHILD_PAGES) {
        const queryParams = {
          modelIds,
          filters,
          page: pageNo,
          size: childrenPageSize || 100,
        };
        const res = await queryClient.fetchQuery({
          queryKey: [FETCH_MODEL_INSTANCES_CURSOR_PAGINATION_KEY, queryParams],
          queryFn: () => fetchModelInstancesCursorPaginationApi(queryParams),
          staleTime: STALE_TIME,
        });
        all.push(...(res.data ?? []));
        hasNext = !!res.hasNext;
        pageNo += 1;
      }

      setTreeData(prev => setChildrenById(prev, record.id, all.map(toRow)));
    },
    [
      queryClient,
      modelIds,
      childKey,
      scopeFilter,
      rootFilter,
      parentArrayField,
      parentField,
      childrenPageSize,
      toRow,
    ]
  );

  return {
    treeData,
    total: rootsMeta?.total ?? 0,
    isLoading: isLoadingModelInstances,
    loadChildren,
  };
};
